import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';

import type { DsTableSort } from '@gde-mit/angular-design-system';
import { SnackBarService } from '@gde-mit/angular-design-system';

import type { DocumentListItem, DocumentStatus } from '@api';

import { TranslocoService } from '@jsverse/transloco';

import { DOCUMENT_ATTACH_I18N_SCOPE } from '../../constants/document-attach-i18n';
import { DOCUMENT_ATTACH_LIBRARY_ERROR_TEST_ID } from '../../constants/document-attach-test-ids';
import type { DocumentLibraryVisibilityFilter } from '../../constants/document-library-visibility-filter.constants';
import { mapDocumentListItemsToTableRows } from '../../utils/document-library-row.util';
import { documentLibrarySortToApi } from '../../utils/document-library-sort-api.util';
import { DOCUMENT_LIST_DEFAULT_PAGE_SIZE, DocumentsApiService } from '../api/documents.api.service';

export const SEARCH_DEBOUNCE_MS = 500;

export type DocumentLibraryListQueryLoadOptions = {
  /** When set, list query is restricted to this status (Kontextus attach dialog). */
  status?: DocumentStatus;
};

@Injectable({ providedIn: 'root' })
export class DocumentLibraryListQueryStore {
  readonly #documentsApi = inject(DocumentsApiService);
  readonly #snackBar = inject(SnackBarService);
  readonly #transloco = inject(TranslocoService);
  readonly #destroyRef = inject(DestroyRef);

  readonly initialLoading = signal(true);
  readonly tableLoading = signal(false);
  readonly initialLoadDone = signal(false);
  readonly searchInput = signal('');
  readonly searchTerm = signal('');
  readonly typeFilter = signal('all');
  readonly visibilityFilter = signal<DocumentLibraryVisibilityFilter>('all');
  readonly pageIndex = signal(0);
  readonly pageSize = signal(DOCUMENT_LIST_DEFAULT_PAGE_SIZE);
  readonly totalCount = signal(0);
  readonly #items = signal<readonly DocumentListItem[]>([]);
  readonly tableSort = signal<DsTableSort[]>([]);
  readonly loadError = signal(false);
  readonly #loadOptions = signal<DocumentLibraryListQueryLoadOptions>({});

  readonly tableRows = computed(() => mapDocumentListItemsToTableRows(this.#items()));

  #searchDebounceTimer: ReturnType<typeof setTimeout> | undefined;
  #loadRequestId = 0;

  constructor() {
    this.#destroyRef.onDestroy(() => this.#clearSearchDebounceTimer());
  }

  setLoadOptions(options: DocumentLibraryListQueryLoadOptions): void {
    this.#loadOptions.set(options);
  }

  onSearchInput(value: string): void {
    this.searchInput.set(value);

    if (value.trim() === '') {
      this.#clearSearchDebounceTimer();
      this.#applySearchTerm('');
      return;
    }

    this.#scheduleDebouncedSearch();
  }

  onTypeFilterChange(fileType: string): void {
    if (this.typeFilter() === fileType) {
      return;
    }

    this.typeFilter.set(fileType);
    this.pageIndex.set(0);
    void this.loadList();
  }

  onVisibilityFilterChange(filter: DocumentLibraryVisibilityFilter): void {
    if (this.visibilityFilter() === filter) {
      return;
    }

    this.visibilityFilter.set(filter);
    this.pageIndex.set(0);
    void this.loadList();
  }

  onPageChanged(index: number): void {
    if (index === this.pageIndex()) {
      return;
    }

    this.pageIndex.set(index);
    void this.loadList();
  }

  onPageSizeChanged(size: number): void {
    if (size === this.pageSize()) {
      return;
    }

    this.pageSize.set(size);
    this.pageIndex.set(0);
    void this.loadList();
  }

  onTableSortChange(sort: DsTableSort[]): void {
    this.tableSort.set(sort);
    this.pageIndex.set(0);
    void this.loadList();
  }

  async loadList(): Promise<void> {
    const requestId = ++this.#loadRequestId;
    const isInitialLoad = !this.initialLoadDone();

    if (isInitialLoad) {
      this.initialLoading.set(true);
    } else {
      this.tableLoading.set(true);
    }

    this.loadError.set(false);

    try {
      const fileType = this.typeFilter() === 'all' ? undefined : this.typeFilter();
      const visibilityFilter = this.visibilityFilter();
      const visibility = visibilityFilter === 'all' ? undefined : visibilityFilter;
      const status = this.#loadOptions().status;
      const { sortBy, sortDir } = documentLibrarySortToApi(this.tableSort());
      const response = await this.#documentsApi.list({
        searchTerm: this.searchTerm().trim() || undefined,
        fileType,
        ...(visibility ? { visibility } : {}),
        ...(status ? { status } : {}),
        page: this.pageIndex() + 1,
        pageSize: this.pageSize(),
        sortBy,
        sortDir,
      });

      if (requestId !== this.#loadRequestId) {
        return;
      }

      this.#items.set(response.items);
      this.totalCount.set(response.totalCount);
    } catch {
      if (requestId !== this.#loadRequestId) {
        return;
      }

      this.loadError.set(true);
      if (isInitialLoad) {
        this.#items.set([]);
        this.totalCount.set(0);
      }

      this.#snackBar.open(
        {
          message: this.#transloco.translate(`${DOCUMENT_ATTACH_I18N_SCOPE}.DOCUMENT_LIBRARY.ERROR_LOAD`),
          testId: DOCUMENT_ATTACH_LIBRARY_ERROR_TEST_ID,
        },
        { duration: 'short' },
      );
    } finally {
      if (requestId === this.#loadRequestId) {
        if (isInitialLoad) {
          this.initialLoading.set(false);
        } else {
          this.tableLoading.set(false);
        }
        this.initialLoadDone.set(true);
      }
    }
  }

  #applySearchTerm(term: string): void {
    if (term === this.searchTerm()) {
      return;
    }

    this.searchTerm.set(term);
    this.pageIndex.set(0);
    void this.loadList();
  }

  #scheduleDebouncedSearch(): void {
    this.#clearSearchDebounceTimer();
    this.#searchDebounceTimer = setTimeout(() => {
      this.#searchDebounceTimer = undefined;
      this.#applySearchTerm(this.searchInput());
    }, SEARCH_DEBOUNCE_MS);
  }

  #clearSearchDebounceTimer(): void {
    if (this.#searchDebounceTimer !== undefined) {
      clearTimeout(this.#searchDebounceTimer);
      this.#searchDebounceTimer = undefined;
    }
  }
}
